import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CalendarClock, CreditCard, LogOut, Lock } from "lucide-react";

export default function TrialExpired() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-pulse text-slate-400">Cargando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.store_locked || user.is_impersonating || user.role === "super_admin") {
    return <Navigate to="/" replace />;
  }

  const retentionDate = user.data_retention_until
    ? new Date(user.data_retention_until).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
    : null;
  const daysLeft = user.retention_days_remaining;
  const urgent = typeof daysLeft === "number" && daysLeft <= 7;
  const canChoosePlan = user.role === "admin";

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="trial-expired-screen">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8 sm:p-10 text-center space-y-6">
        <img src="/logo.png" alt="SkiFlow Rental" className="h-10 mx-auto" />
        <div className="mx-auto h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-8 w-8 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Tu prueba ha terminado</h1>
          <p className="text-slate-600 mt-2">
            El periodo de prueba de <strong>{user.store_name || "tu tienda"}</strong> ha finalizado.
            Elige un plan para seguir operando exactamente donde lo dejaste.
          </p>
        </div>

        {retentionDate && (
          <div
            className={`rounded-xl border p-4 text-left flex gap-3 ${urgent ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}
            data-testid="retention-notice"
          >
            <CalendarClock className={`h-6 w-6 flex-shrink-0 ${urgent ? "text-red-600" : "text-amber-600"}`} />
            <div>
              <p className={`text-sm font-semibold ${urgent ? "text-red-800" : "text-amber-800"}`}>
                Guardamos tus datos hasta el {retentionDate}
                {typeof daysLeft === "number" && daysLeft >= 0 && (
                  <span className={`ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-bold ${urgent ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                    {daysLeft === 0 ? "¡Último día!" : `Quedan ${daysLeft} días`}
                  </span>
                )}
              </p>
              <p className={`text-xs mt-1 ${urgent ? "text-red-700" : "text-amber-700"}`}>
                Elige un plan antes de esa fecha para recuperar todo lo que creaste durante la prueba
                (inventario, clientes, alquileres y configuración). Después, tus datos se eliminarán definitivamente.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {canChoosePlan ? (
            <Button
              className="w-full h-12 text-base font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
              onClick={() => navigate("/seleccionar-plan")}
              data-testid="choose-plan-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              Elegir plan y continuar
            </Button>
          ) : (
            <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3" data-testid="staff-locked-msg">
              Pide al administrador de tu tienda que elija un plan para reactivar el acceso.
            </p>
          )}
          <Button variant="outline" className="w-full h-11 rounded-xl" onClick={logout} data-testid="locked-logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
}
